import { revoke_contact_display_payload } from '../../../utils/contact/contact_access';
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
    Alert_,
    AlertContext,
    Card,
    Grey_Link,
    Main_Plain,
    PageAlwaysVisible,
    PageSidebar,
    PageTitle,
    Partial_Loader,
    Section_Title,
    Slider_
} from "monica-alexandria";
import store from "../../../redux/store";
import { config } from "../../../config";
import {
    get_hr_member_clock,
    open_hr_member_clock_events,
    pause_hr_member_clock,
    read_dolores_team_users,
    resume_hr_member_clock
} from "../../../services/api/HumanResources";
import {
    create_brand_calendar,
    create_brand_comment,
    create_offer_from_brand,
    create_sass_from_brand,
    assign_brand_seller,
    edit_prospect_brand,
    get_sales_prospects,
    read_brand_row_with_seller,
    read_prospect_brand_rows,
    update_brand_status
} from "../../../services/api/Prospects";
import { save_registration } from "../../../services/api/Leads";
import { Toolbar_ } from "../../partials/navigation/Toolbar_.jsx";
import { alertMessage } from "../../../helpers/messagesAlerts";
import { Sales_Lead_Form_Edit } from "./components/Sales_Lead_Form_Edit";
import { Sales_Board } from "./components/Sales_Board";
import { Sales_Header } from "./components/Sales_Header";
import { Sales_Clock_Bar } from "./components/Sales_Clock_Bar";
import {
    filter_leads,
    filter_leads_by_members,
    pageSize,
    read_board_columns,
    read_is_assigned_lead,
    read_is_backlog_lead,
    read_is_in_progress_lead,
    read_is_phone_only_lead,
    read_is_sales_board_visible_lead,
    read_lead_assignee,
    read_lead_name,
    read_member_assignment_payload,
    read_member_keys,
    read_member_select_value,
    read_prospect_status,
    read_sales_board_members,
    read_sales_clock_lock_enabled,
    read_sass_active_customer_state,
    prospectLeadBacklogStatus,
    prospectLeadAssignedStatus,
    prospectLeadContractSentStatus,
    prospectLeadLostStatus,
    prospectLeadProgressActionCreateOffer,
    prospectLeadProgressActionNotAnswered,
    prospectLeadProgressActionNotInterested,
    prospectLeadProgressActionReschedule,
    prospectLeadProgressActionSass,
    prospectLeadProgressActionUpdate,
    prospectLeadInProgressStatus,
    prospectLeadStallingStatus
} from "./utils/sales_helpers";
import { read_seller_member_by_sub_id } from "../../../utils/hr/seller_identity";

const ProspectLeads = styled.div`
    width: 100%;
    max-width: 100%;
    overflow: hidden;
`;

// Reads one stable request key so stale pages cannot replace another seller filter.
const read_sales_seller_filter_key = (sellerKeys = []) => {
    return [...new Set((sellerKeys || []).map((sellerKey) => `${sellerKey || ""}`.trim().toLowerCase()).filter(Boolean))]
        .sort()
        .join("|");
};

const ProspectLeadsContent = styled.div`
    display: grid;
    gap: 2rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
`;

const ProspectLeadsEmpty = styled.div`
    padding: var(--largePads);
    border-radius: var(--normalRadius);
    background: ${p => p.theme.low};
`;

const ProspectLeadsLoadMoreTrigger = styled.div`
    display: flex;
    justify-content: center;
    padding: var(--normalPads);
`;

const ProspectLeadSubmissionContent = styled.div`
    pointer-events: ${p => p.locked ? "none" : "auto"};
    opacity: ${p => p.locked ? 0.6 : 1};
    transition: opacity 0.2s ease;
`;

const ProspectLeadsBoardStage = styled.div`
    position: relative;
    min-height: 42rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
`;

const ProspectLeadsBoardStageContent = styled.div`
    filter: ${p => p.locked ? "blur(0.8rem)" : "none"};
    opacity: ${p => p.locked ? 0.35 : 1};
    pointer-events: ${p => p.locked ? "none" : "auto"};
    transition: filter 0.25s ease, opacity 0.25s ease;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
`;

const ProspectLeadsClockOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--largePads);
    background: ${p => p.theme.overlay};
    backdrop-filter: blur(1.4rem);
`;

const ProspectLeadsClockCard = styled(Card)`
    width: min(46rem, 100%);
    padding: var(--largePads);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1.2rem;
    background: ${p => p.theme.background};
`;

/**
 * Reads the most useful server error for failed backend requests.
 */
const read_request_error_message = (error, fallback) => {
    const responseData = error?.response?.data;
    const serverMessage = responseData?.msg || responseData?.message;

    if (serverMessage) {
        return serverMessage;
    }

    return error?.message || fallback;
};

/**
 * Reads the most useful server error for failed SASS saves.
 */
const read_sass_request_error_message = (error, fallback) => {
    const responseData = error?.response?.data;

    if (typeof responseData === "string" && responseData.includes("Cannot POST /sass/import")) {
        return "Offers backend is missing the SASS import API route";
    }

    if (typeof responseData === "string" && responseData.includes("Cannot POST")) {
        return "Leads backend is missing the SASS API route";
    }

    return read_request_error_message(error, fallback);
};

const silentSalesClockSyncIntervalMs = 10 * 60 * 1000;
const salesPushApplicationServerKey = "BHF36REe2So0v6WSLGT8r6nL0kqUotyYvwLG1NNjtKq6dyDKlm_mhlbdCI6czU0sw9Mc1WANWvrdn_pHgfchz-A";

/**
 * Registers Sales board browser push through the legacy notification endpoint.
 */
const register_sales_push_notifications = () => {
    let registration;

    if ('serviceWorker' in navigator && 'PushManager' in window) {
        navigator.serviceWorker.register('/service-worker.js')
            .then((reg) => {
                registration = reg;

                return reg.pushManager.getSubscription();
            })
            .then((subscription) => {
                if (!subscription) {
                    return registration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: salesPushApplicationServerKey
                    });
                }

                return subscription;
            })
            .then((subscription) => {
                save_registration(JSON.stringify(subscription));
            })
            .catch((error) => console.error('Push registration failed:', error));
    }
};

const read_clock_ticking = (clockState = {}) => {
    return Boolean(clockState?.ticking || clockState?.clock?.ticking);
};

const read_clock_loaded = (clockState = {}) => {
    return clockState?.loaded === true;
};

const read_clock_blocked = (clockState = {}) => {
    return read_clock_loaded(clockState) && !read_clock_ticking(clockState);
};

const read_normalized_member_value = (value = "") => {
    return `${value || ""}`.trim().replace(/\s+/g, " ").toLowerCase();
};

const read_member_match_values = (member = {}) => {
    return [
        read_member_select_value(member),
        member?.username,
        member?.preferred_username,
        member?.preferredUsername,
        member?.email,
        member?.userId,
        member?.user_id,
        member?.id,
        member?._id,
        member?.sub,
        member?.subId,
        member?.subscriptionId
    ].map((value) => read_normalized_member_value(value)).filter((value) => value);
};

const read_current_sales_member = (members = [], user = {}) => {
    const currentUserKeys = read_member_match_values(user);

    return (members || []).find((member) => {
        const memberKeys = read_member_match_values(member);

        return memberKeys.some((memberKey) => currentUserKeys.includes(memberKey));
    }) || user || {};
};

const read_current_user_in_board_team = (members = [], user = {}) => {
    const currentUserKeys = read_member_match_values(user);

    return (members || []).some((member) => {
        const memberKeys = read_member_match_values(member);

        return memberKeys.some((memberKey) => currentUserKeys.includes(memberKey));
    });
};

const read_clock_member_key = (member = {}, user = {}) => {
    return [
        member?.subId,
        member?.subscriptionId,
        member?.userId,
        member?.id,
        member?._id,
        member?.email,
        member?.username,
        user?.sub,
        user?.userId,
        user?.email,
        user?.username,
        user?.preferred_username
    ].filter((valueItem) => valueItem).join("|");
};

const read_lead_assignment_matches_member = (lead = {}, member = {}, user = {}) => {
    const assigneeKeys = read_member_keys(read_lead_assignee(lead));
    const memberKeys = [
        ...read_member_keys(member),
        ...read_member_match_values(user)
    ];

    return assigneeKeys.some((assigneeKey) => memberKeys.includes(assigneeKey));
};

/**
 * Finds the team member selected from the seller dropdown.
 */
const read_team_member_from_seller_value = (members = [], sellerValue = "") => {
    return read_seller_member_by_sub_id(members, sellerValue);
};

/**
 * Reads the logged-in user as the comment author payload.
 */
const read_comment_author = () => {
    const user = store.getState()?.user?.user;
    const userId = user?.userId || user?.sub || "";
    const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ");

    return {
        userId,
        name,
        avatarUrl: userId ? `${config.minio}/erisedev/${userId}/avatar.png` : ""
    };
};

/**
 * Reads the stable board row key used to prevent duplicate Sales cards.
 */
const read_sales_row_key = (row) => {
    return `${row?.brandId || row?._id || row?.id || ""}`.trim();
};

/**
 * Reads a sanitized Sales row snapshot for browser debugging.
 */
const read_sales_debug_row = (row = {}) => {
    return {
        rowKey: read_sales_row_key(row),
        prospectId: row?.prospectId || "",
        brandId: row?.brandId || row?._id || "",
        status: row?.status || row?.prospectStatus || row?.brandRecord?.status || "",
        inSales: row?.inSales === true || row?.brandRecord?.inSales === true,
        hasBrandName: Boolean(`${row?.brand || row?.brandRecord?.name || ""}`.trim()),
        clientBrand: row?.client === true || row?.brandRecord?.client === true,
        hasActiveSalesSession: Boolean(row?.activeSalesSession?.sessionId || row?.brandRecord?.activeSalesSession?.sessionId),
        commentsCount: Array.isArray(row?.comments) ? row.comments.length : 0,
        historyCount: Array.isArray(row?.histories) ? row.histories.length : 0,
        timelineCount: Array.isArray(row?.timeline) ? row.timeline.length : 0
    };
};

/**
 * Prints one Sales flow checkpoint without customer contact details.
 */
const log_sales_ui_debug = (step, payload = {}) => {
    console.log("[Niffler Sales UI]", step, payload);
};

/**
 * Checks whether two board rows point at the same saved Prospect brand.
 */
const read_sales_rows_match = (firstRow, secondRow) => {
    const firstKey = read_sales_row_key(firstRow);
    const secondKey = read_sales_row_key(secondRow);

    return Boolean(firstKey && secondKey && firstKey === secondKey);
};

/**
 * Checks whether one board row matches a stored active row key.
 */
const read_sales_row_matches_key = (row, rowKey) => {
    return Boolean(read_sales_row_key(row) && read_sales_row_key(row) === rowKey);
};

/**
 * Keeps one visible Sales card per saved Prospect brand row.
 */
const read_unique_sales_rows = (rows) => {
    const seenRowKeys = new Set();

    return (rows || []).filter((row) => {
        const rowKey = read_sales_row_key(row);

        if (!rowKey) {
            return true;
        }

        if (seenRowKeys.has(rowKey)) {
            return false;
        }

        seenRowKeys.add(rowKey);
        return true;
    });
};

/**
 * Replaces all brand rows that belong to one updated prospect.
 */
const read_rows_with_updated_prospect = (currentRows, updatedProspect) => {
    const updatedRows = read_prospect_brand_rows([updatedProspect], true);
    const remainingRows = currentRows.filter((row) => row?.prospectId !== updatedProspect?._id);

    return read_unique_sales_rows([...remainingRows, ...updatedRows]);
};

/**
 * Reads the updated active row from a saved prospect response.
 */
const read_updated_active_row = (updatedProspect, activeRow) => {
    const updatedRows = read_prospect_brand_rows([updatedProspect]);

    return updatedRows.find((row) => read_sales_rows_match(row, activeRow)) || null;
};

/**
 * Renders the dedicated admin board for leads created from the NEW_LEAD page.
 */
export default function SALES() {
    const { t } = useTranslation();
    const { alerts, setAlerts } = useContext(AlertContext);
    const navigate = useNavigate();
    const { lang } = useParams();
    const routeLanguage = lang || "en";
    const dragLeadIdRef = useRef("");
    const dragClickLockRef = useRef(false);
    const createOfferInProgressRef = useRef(false);
    const salesSubmissionRef = useRef("");
    const loadMoreTriggerRef = useRef(null);
    const loadingMoreRef = useRef(false);
    const salesRequestSequenceRef = useRef(0);
    const salesSellerFilterKeyRef = useRef("");
    const [sidebar, setSidebar] = useState(true);
    const [leads, setLeads] = useState([]);
    const [totalLeads, setTotalLeads] = useState(0);
    const [pageNo, setPageNo] = useState(1);
    const [hasMoreSalesPages, setHasMoreSalesPages] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [dragLeadId, setDragLeadId] = useState("");
    const [activeLeadId, setActiveLeadId] = useState("");
    const [draftLead, setDraftLead] = useState(null);
    const [pendingPhoneValue, setPendingPhoneValue] = useState("");
    const [progressAction, setProgressAction] = useState(prospectLeadProgressActionNotAnswered);
    const [progressActionDate, setProgressActionDate] = useState("");
    const [progressActionComment, setProgressActionComment] = useState("");
    const [progressActionError, setProgressActionError] = useState("");
    const [sassCallerValue, setSassCallerValue] = useState("");
    const [teamMembers, setTeamMembers] = useState([]);
    const [boardMembers, setBoardMembers] = useState([]);
    const [selectedTeamMemberKeys, setSelectedTeamMemberKeys] = useState([]);
    const [memberClock, setMemberClock] = useState({ loaded: false });
    const [clockActionLoading, setClockActionLoading] = useState(false);
    const [salesSubmissionAction, setSalesSubmissionAction] = useState("");
    const [claimingLeadId, setClaimingLeadId] = useState("");
    const currentUser = store.getState()?.user?.user || {};
    const activeClockMember = read_current_sales_member(teamMembers, currentUser);
    const activeClockMemberKey = read_clock_member_key(activeClockMember, currentUser);
    const currentUserIsBoardTeamMember = read_current_user_in_board_team(teamMembers, currentUser);
    const salesClockLockEnabled = read_sales_clock_lock_enabled();
    const clockBlocked = salesClockLockEnabled && read_clock_blocked(memberClock);
    const visibleLeads = filter_leads_by_members(filter_leads(leads, searchTerm).filter((lead) => read_is_sales_board_visible_lead(lead)), selectedTeamMemberKeys);
    const boardColumns = read_board_columns(visibleLeads);
    const currentUserHasInProgressLead = leads.some((lead) => {
        return read_is_in_progress_lead(lead) && read_lead_assignment_matches_member(lead, activeClockMember, currentUser);
    });
    const clockNeedsInProgressAttention = Boolean(salesClockLockEnabled && currentUserIsBoardTeamMember && read_clock_ticking(memberClock) && !currentUserHasInProgressLead);
    const activeLead = leads.find((lead) => read_sales_row_matches_key(lead, activeLeadId));
    const activeSliderLead = draftLead || activeLead;
    const activeLeadIsBacklog = Boolean(activeSliderLead) && read_is_backlog_lead(activeSliderLead);
    const activeLeadIsAssigned = Boolean(activeSliderLead) && read_is_assigned_lead(activeSliderLead);
    const activeLeadIsInProgress = Boolean(activeSliderLead) && read_is_in_progress_lead(activeSliderLead);
    const activeLeadIsPhoneOnly = Boolean(activeSliderLead) && read_is_phone_only_lead(activeSliderLead);
    const salesSubmissionLocked = Boolean(salesSubmissionAction);
    const showLoadMore = hasMoreSalesPages && !activeSliderLead;

    /**
     * Runs one Sales write transaction at a time so repeated clicks cannot append duplicate timeline actions.
     */
    const run_sales_submission = async (action, submission) => {
        if (salesSubmissionRef.current) {
            log_sales_ui_debug("sales_submission:blocked_duplicate", {
                requestedAction: action,
                pendingAction: salesSubmissionRef.current,
                active: read_sales_debug_row(activeSliderLead)
            });
            return false;
        }

        salesSubmissionRef.current = action;
        setSalesSubmissionAction(action);

        try {
            return await submission();
        } finally {
            if (salesSubmissionRef.current === action) {
                salesSubmissionRef.current = "";
                setSalesSubmissionAction("");
            }
        }
    };

    /**
     * Shows a non-blocking warning when linked app sync did not confirm.
     */
    const show_sync_warning = (sync) => {
        if (!sync?.warning) {
            return;
        }

        alertMessage(alerts, setAlerts, sync.warning, "Warning");
        setProgressActionError(sync.warning);
    };

    /**
     * Keeps the SASS conflict visible both as a notification and in the form.
     */
    const show_sass_active_customer_warning = (message) => {
        const translatedMessage = t(message);

        alertMessage(alerts, setAlerts, translatedMessage, "Warning");
        setProgressActionError(translatedMessage);
    };

    /**
     * Notifies Sales about the provider already running without blocking the remaining sale.
     */
    const show_sass_active_customer_notice = (message) => {
        alertMessage(alerts, setAlerts, t(message), "Warning");
    };

    /**
     * Reads the unique phone values already stored on a lead.
     */
    const read_lead_phone_values = (lead) => {
        const values = [
            lead?.phone,
            ...(lead?.phoneList || []),
            ...(lead?.phones || [])
        ].filter(Boolean);

        return values.filter((value, valueIndex) => values.indexOf(value) === valueIndex);
    };

    /**
     * Adds the current typed phone input to the lead before Done saves.
     */
    const read_done_lead = (lead) => {
        const cleanPhone = pendingPhoneValue.trim();
        const phoneValues = read_lead_phone_values(lead);
        const nextPhones = cleanPhone && !phoneValues.includes(cleanPhone) ? [...phoneValues, cleanPhone] : phoneValues;

        return {
            ...lead,
            phone: nextPhones[0] || "",
            phoneList: [],
            phones: nextPhones
        };
    };

    /**
     * Resets the In Progress save outcome controls back to the default state.
     */
    const reset_progress_action = () => {
        setProgressAction(prospectLeadProgressActionNotAnswered);
        setProgressActionDate("");
        setProgressActionComment("");
        setProgressActionError("");
        setSassCallerValue("");
    };

    /**
     * Updates the local HR clock snapshot from API or socket events.
     */
    const update_member_clock_state = (clockState = {}) => {
        setMemberClock(clockState || { loaded: false });
    };

    /**
     * Loads the latest HR clock snapshot for the current Sales member.
     */
    const load_member_clock = async (memberItem = {}) => {
        const loadedClock = await get_hr_member_clock(currentUser, memberItem);

        update_member_clock_state(loadedClock || { loaded: false });

        return loadedClock;
    };

    /**
     * Sends pause or resume to HR and reuses the same member clock state the socket updates.
     */
    const toggle_member_clock = async () => {
        if (clockActionLoading) {
            return;
        }

        const pausingClock = read_clock_ticking(memberClock || {});
        const clockAction = pausingClock ? pause_hr_member_clock : resume_hr_member_clock;

        setClockActionLoading(true);

        try {
            const updatedClock = await clockAction(currentUser, activeClockMember || {});

            if (!updatedClock) {
                return;
            }

            update_member_clock_state(updatedClock);
        } finally {
            setClockActionLoading(false);
        }
    };

    /**
     * Keeps CRM contact arrays unique before they are sent into Godfather.
     */
    const read_unique_contact_values = (values) => {
        return values
            .flat()
            .filter(Boolean)
            .map((value) => `${value || ""}`.trim())
            .filter(Boolean)
            .filter((value, valueIndex, list) => list.indexOf(value) === valueIndex);
    };

    /**
     * Reads every email value returned by the create-offer endpoint.
     */
    const read_offer_response_emails = (createOfferResponse) => {
        const offer = createOfferResponse?.offer || {};
        const lead = offer?.lead || createOfferResponse?.lead || {};

        return read_unique_contact_values([
            offer?.email,
            lead?.email,
            lead?.emails || []
        ]).map((email) => email.toLowerCase());
    };

    /**
     * Reads every phone value returned by the create-offer endpoint.
     */
    const read_offer_response_phones = (createOfferResponse) => {
        const offer = createOfferResponse?.offer || {};
        const lead = offer?.lead || createOfferResponse?.lead || {};

        return read_unique_contact_values([
            offer?.phone,
            lead?.phone,
            lead?.phones || []
        ]);
    };

    /**
     * Maps the CRM create-offer response to the Godfather draft shape.
     */
    const read_godfather_offer_prefill = (createOfferResponse) => {
        const offerLanguage = "el";
        const apiPrefill = createOfferResponse?.prefill;
        const offer = createOfferResponse?.offer || {};
        const lead = offer?.lead || createOfferResponse?.lead || {};
        const category = apiPrefill?.typesData?.category
            || apiPrefill?.category
            || apiPrefill?.typesData?.industry
            || apiPrefill?.industry
            || offer?.category
            || offer?.industry
            || lead?.category
            || lead?.industry
            || activeSliderLead?.category
            || activeSliderLead?.industry
            || "";
        const subcategory = apiPrefill?.typesData?.subcategory
            || apiPrefill?.subcategory
            || offer?.subcategory
            || lead?.subcategory
            || activeSliderLead?.subcategory
            || "";

        if (apiPrefill) {
            const {
                sourceCreateOfferResponse,
                sourceLead,
                sourceOffer,
                ...compactTypesData
            } = apiPrefill?.typesData || {};

            return {
                ...apiPrefill,
                category,
                subcategory,
                industry: category,
                offerLanguage,
                typesData: {
                    ...compactTypesData,
                    category,
                    subcategory,
                    industry: category,
                    offerLanguage
                }
            };
        }

        const clientEmails = read_offer_response_emails(createOfferResponse);
        const clientPhones = read_offer_response_phones(createOfferResponse);
        const clientName = offer?.brandName || lead?.brand || offer?.fullName || lead?.fullName || read_lead_name(activeSliderLead);
        const contactName = offer?.fullName || lead?.fullName || read_lead_name(activeSliderLead);
        const clientHeadquarters = offer?.location || lead?.location || activeSliderLead?.location || "";
        const offerCode = "";
        const sourceProspectId = offer?.prospectId || lead?.prospectId || activeSliderLead?.prospectId || "";
        const sourceBrandId = offer?.brandId || lead?.brandId || activeSliderLead?.brandId || activeSliderLead?._id || "";
        const sourceSystem = sourceProspectId && sourceBrandId ? "niffler-prospect" : "niffler";

        return {
            selectedTypes: ["marketing"],
            clientEmails,
            category,
            subcategory,
            industry: category,
            clientName,
            clientHeadquarters,
            clientVatNumber: "",
            offerLanguage,
            code: offerCode,
            typesData: {
                selectedTypes: ["marketing"],
                clientEmails,
                category,
                subcategory,
                industry: category,
                clientName,
                clientHeadquarters,
                clientVatNumber: "",
                offerLanguage,
                invoiceEnabled: true,
                offerCode,
                sourceSystem,
                sourceLeadId: offer?.leadId || lead?.id || activeSliderLead?._id || "",
                sourceProspectId,
                sourceBrandId,
                draftOfferId: offer?.draftOfrid || lead?.draftOfrid || "",
                sourceOfferId: offerCode,
                contactName,
                contactPhone: clientPhones[0] || "",
                contactPhones: clientPhones,
                submittedBy: lead?.submittedBy || activeSliderLead?.submittedBy || "",
                submittedByName: lead?.submittedByName || activeSliderLead?.submittedByName || "",
                platformSource: lead?.platformSource || activeSliderLead?.platformSource || ""
            }
        };
    };

    /**
     * Creates one handoff id used to match the target Godfather tab.
     */
    const create_offer_handoff_id = () => {
        return `niffler-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    };

    /**
     * Reads the Godfather new-offer route for a pending CRM handoff.
     */
    const read_godfather_new_offer_url = (handoffId) => {
        const searchParams = new URLSearchParams();

        searchParams.set("handoff", "niffler");
        searchParams.set("handoffId", handoffId);

        return `${config.godfather}/${routeLanguage}/new-offer?${searchParams.toString()}`;
    };

    /**
     * Reads the direct Godfather route that can create the draft without postMessage.
     */
    const read_godfather_prefill_offer_url = (createOfferResponse) => {
        const searchParams = new URLSearchParams();

        searchParams.set("prefill", JSON.stringify(read_godfather_offer_prefill(createOfferResponse)));

        return `${config.godfather}/${routeLanguage}/new-offer?${searchParams.toString()}`;
    };

    /**
     * Reads the target origin allowed to receive the Godfather handoff message.
     */
    const read_godfather_origin = () => {
        try {
            return new URL(config.godfather).origin;
        } catch (error) {
            return "*";
        }
    };

    /**
     * Opens the Godfather tab synchronously before the async create-offer request.
     */
    const open_pending_offer_window = (handoffId) => {
        const pendingWindow = window.open(read_godfather_new_offer_url(handoffId), "_blank");

        pendingWindow?.focus?.();

        return pendingWindow;
    };

    /**
     * Closes the pending offer tab when offer creation fails before redirect.
     */
    const close_pending_offer_window = (pendingWindow) => {
        if (pendingWindow && !pendingWindow.closed) {
            pendingWindow.close();
        }
    };

    /**
     * Sends the completed draft to Godfather even when the first popup was blocked.
     */
    const redirect_godfather_offer_prefill = (createOfferResponse, pendingWindow) => {
        if (pendingWindow && !pendingWindow.closed) {
            pendingWindow.focus?.();
            return;
        }

        const offerUrl = read_godfather_prefill_offer_url(createOfferResponse);

        window.open(offerUrl, "_self");
    };

    /**
     * Posts the completed create-offer response into the already-open Godfather tab.
     */
    const post_godfather_offer_prefill = (createOfferResponse, pendingWindow, handoffId, attempt = 0) => {
        if (!pendingWindow || pendingWindow.closed) {
            return;
        }

        pendingWindow.postMessage({
            type: "godfather_offer_prefill",
            handoffId,
            prefill: read_godfather_offer_prefill(createOfferResponse)
        }, read_godfather_origin());

        if (attempt >= 20) {
            return;
        }

        window.setTimeout(() => {
            post_godfather_offer_prefill(createOfferResponse, pendingWindow, handoffId, attempt + 1);
        }, 250);
    };

    /**
     * Reads whether the selected brand can be exported into Agency.
     */
    const read_contract_sent_ready = () => {
        const emails = [
            activeSliderLead?.email,
            ...(activeSliderLead?.emailList || []),
            ...(activeSliderLead?.emails || [])
        ].filter((email) => `${email || ""}`.trim());
        const hasBrand = Boolean(`${activeSliderLead?.brand || ""}`.trim());
        const hasCategory = Boolean(`${activeSliderLead?.category || ""}`.trim());

        return hasBrand && emails.length > 0 && hasCategory;
    };

    /**
     * Reads whether every brand field required for the temporary SASS flow is filled.
     */
    const read_sass_fields_ready = () => {
        const emails = [
            activeSliderLead?.email,
            ...(activeSliderLead?.emailList || []),
            ...(activeSliderLead?.emails || [])
        ].filter((email) => `${email || ""}`.trim());
        const phones = [
            activeSliderLead?.phone,
            ...(activeSliderLead?.phoneList || []),
            ...(activeSliderLead?.phones || [])
        ].filter((phone) => `${phone || ""}`.trim());
        const hasName = Boolean(`${read_lead_name(activeSliderLead) || ""}`.trim());
        const hasBrand = Boolean(`${activeSliderLead?.brand || ""}`.trim());
        const hasCategory = Boolean(`${activeSliderLead?.category || ""}`.trim());
        const hasSubcategory = Boolean(`${activeSliderLead?.subcategory || ""}`.trim());
        const hasLocation = Boolean(`${activeSliderLead?.location || ""}`.trim());

        return hasName && hasBrand && emails.length > 0 && phones.length > 0 && hasCategory && hasSubcategory && hasLocation;
    };

    /**
     * Updates the selected In Progress outcome and clears stale required fields.
     */
    const change_progress_action = (action) => {
        log_sales_ui_debug("change_progress_action", {
            action,
            active: read_sales_debug_row(activeSliderLead)
        });

        if (action === prospectLeadProgressActionCreateOffer && !read_contract_sent_ready()) {
            setProgressActionError(t("Brand, email and category are required for Agency"));
            return;
        }

        setProgressAction(action);
        setProgressActionDate("");
        setProgressActionComment("");
        setProgressActionError("");
        setSassCallerValue("");
    };

    /**
     * Opens Godfather and closes Sales after the offer payload is ready.
     */
    const start_create_offer_handoff = async () => {
        log_sales_ui_debug("create_offer_handoff:start", {
            active: read_sales_debug_row(activeSliderLead),
            progressAction
        });

        if (createOfferInProgressRef.current) {
            log_sales_ui_debug("create_offer_handoff:blocked_duplicate", {
                active: read_sales_debug_row(activeSliderLead)
            });
            return false;
        }

        createOfferInProgressRef.current = true;
        const handoffId = create_offer_handoff_id();
        let pendingOfferWindow = null;

        try {
            pendingOfferWindow = open_pending_offer_window(handoffId);
            log_sales_ui_debug("create_offer_handoff:pending_window_opened", {
                handoffId,
                active: read_sales_debug_row(activeSliderLead)
            });
            const saved = await edit_active_lead(activeSliderLead);

            if (!saved) {
                log_sales_ui_debug("create_offer_handoff:preliminary_edit_failed", {
                    handoffId,
                    active: read_sales_debug_row(activeSliderLead)
                });
                close_pending_offer_window(pendingOfferWindow);
                return false;
            }

            log_sales_ui_debug("create_offer_handoff:preliminary_edit_saved", {
                handoffId,
                saved: read_sales_debug_row(saved)
            });

            return await save_create_offer_progress_action(saved, pendingOfferWindow, handoffId);
        } finally {
            log_sales_ui_debug("create_offer_handoff:finish", {
                handoffId,
                active: read_sales_debug_row(activeSliderLead)
            });
            createOfferInProgressRef.current = false;
        }
    };

    /**
     * Stores the selected reschedule date as an ISO string.
     */
    const change_progress_action_date = (date) => {
        setProgressActionDate(date ? date.toISOString() : "");
        setProgressActionError("");
    };

    /**
     * Stores the required Lost comment draft.
     */
    const change_progress_action_comment = (comment) => {
        setProgressActionComment(comment);
        setProgressActionError("");
    };

    /**
     * Stores the selected SASS meeting caller required by Godfather.
     */
    const change_sass_caller = (sellerValue) => {
        setSassCallerValue(sellerValue);
        setProgressActionError("");
    };

    /**
     * Reads plain text from Quill HTML for required comment validation.
     */
    const read_progress_comment_text = () => {
        return progressActionComment
            .replace(/<[^>]*>/g, " ")
            .replace(/&nbsp;/g, " ")
            .trim();
    };

    /**
     * Checks whether the selected calendar day is before today.
     */
    const read_is_previous_progress_date = () => {
        const selectedDate = new Date(progressActionDate);
        const todayDate = new Date();

        selectedDate.setHours(0, 0, 0, 0);
        todayDate.setHours(0, 0, 0, 0);

        return selectedDate.getTime() < todayDate.getTime();
    };

    /**
     * Reads the missing required field message for the selected In Progress outcome.
     */
    const read_progress_action_error = () => {
        if (progressAction === prospectLeadProgressActionCreateOffer && !read_contract_sent_ready()) {
            return t("Brand, email and category are required for Agency");
        }

        if (progressAction === prospectLeadProgressActionSass && !read_sass_fields_ready()) {
            return t("Full name, brand, email, phone, category, subcategory and location are required for SASS");
        }

        if (progressAction === prospectLeadProgressActionSass && !progressActionDate) {
            return t("Choose meeting date and time");
        }

        if (progressAction === prospectLeadProgressActionSass && !sassCallerValue) {
            return t("Choose caller");
        }

        if (progressAction === prospectLeadProgressActionSass && read_is_previous_progress_date()) {
            return t("Choose today or a future date");
        }

        if (progressAction === prospectLeadProgressActionReschedule && !progressActionDate) {
            return t("Choose a date and time");
        }

        if (progressAction === prospectLeadProgressActionReschedule && read_is_previous_progress_date()) {
            return t("Choose today or a future date");
        }

        if (progressAction === prospectLeadProgressActionNotInterested && !read_progress_comment_text()) {
            return t("Comment is required");
        }

        return "";
    };

    /**
     * Formats a saved reschedule date for the attached action comment.
     */
    const read_reschedule_comment = () => {
        const date = new Date(progressActionDate);

        return `${t("Rescheduled for")} ${date.toLocaleString()}`;
    };

    /**
     * Loads one page of assigned prospect brands for the Sales board.
     */
    const load_new_leads = async (nextPageNo, mode, sellerKeys = selectedTeamMemberKeys) => {
        const requestedSellerFilterKey = read_sales_seller_filter_key(sellerKeys);

        if (requestedSellerFilterKey !== salesSellerFilterKeyRef.current) {
            return false;
        }

        const requestSequence = salesRequestSequenceRef.current + 1;

        salesRequestSequenceRef.current = requestSequence;
        setLoading(true);

        const response = await get_sales_prospects(nextPageNo, pageSize, sellerKeys).catch(() => false);

        if (
            requestSequence !== salesRequestSequenceRef.current
            || requestedSellerFilterKey !== salesSellerFilterKeyRef.current
        ) {
            return false;
        }

        const fetchedLeads = read_prospect_brand_rows(response?.prospects || [], true);
        const fetchedTotal = response?.count || 0;
        const nextLeads = {
            append: read_unique_sales_rows([...leads, ...fetchedLeads]),
            replace: read_unique_sales_rows(fetchedLeads)
        }[mode];
        const addedUniqueLeads = mode === "replace" || nextLeads.length > leads.length;
        const backendHasMorePages = nextPageNo * pageSize < fetchedTotal;

        setLeads(nextLeads);
        setBoardMembers(read_sales_board_members(response?.assignedSellers, nextLeads));
        setTotalLeads(fetchedTotal);
        setHasMoreSalesPages(backendHasMorePages && addedUniqueLeads);
        setPageNo(nextPageNo);
        setLoading(false);

        return true;
    };

    /**
     * Loads the next sales page once the board bottom becomes visible.
     */
    const load_more_new_leads = () => {
        if (!showLoadMore || loading || loadingMoreRef.current) {
            return;
        }

        loadingMoreRef.current = true;

        load_new_leads(pageNo + 1, "append")
            .finally(() => {
                loadingMoreRef.current = false;
            });
    };

    /**
     * Refreshes only the cross-page member filter after a terminal Sales outcome.
     */
    const refresh_board_members = async () => {
        const response = await get_sales_prospects(1, 1).catch(() => false);

        if (!response) {
            return;
        }

        setBoardMembers(read_sales_board_members(response?.assignedSellers, read_prospect_brand_rows(response?.prospects || [], true)));
    };

    /**
     * Clears the assignee when a lead is returned to Backlog.
     */
    const read_status_update = (lead, prospectStatus) => {
        const sellerPayload = prospectStatus === prospectLeadBacklogStatus ? null : read_lead_assignee(lead);

        return read_brand_row_with_seller(lead, sellerPayload, prospectStatus);
    };

    /**
     * The backend shared lock is the only source of truth for In Progress conflicts.
     */
    const read_in_progress_transition_allowed = async () => {
        return true;
    };

    /**
     * Reads the specific backend block reason before falling back to refresh guidance.
     */
    const read_move_failure_message = (result) => {
        if (result?.reason === "inprogress_lock_conflict") {
            return result?.msg || result?.message || t("Clear your inprogress before you add a new one");
        }

        return result?.msg || result?.message || t("The card could not be moved. Refresh and try again.");
    };

    /**
     * Moves a lead between board columns and persists its prospect status.
     */
    const move_lead_to_status = async (lead, prospectStatus) => {
        if (read_is_backlog_lead(lead) && read_prospect_status(lead) !== prospectStatus) {
            return false;
        }

        if (read_is_assigned_lead(lead) && prospectStatus !== prospectLeadInProgressStatus) {
            return false;
        }

        const inProgressTransitionAllowed = await read_in_progress_transition_allowed(lead, prospectStatus);

        if (!inProgressTransitionAllowed) {
            return false;
        }

        const previousLeads = leads;
        const previousActiveLeadId = activeLeadId;
        const previousDraftLead = draftLead;
        const targetLeadKey = read_sales_row_key(lead);
        const updatedLeads = leads.map((leadItem) => {
            if (!read_sales_rows_match(leadItem, lead)) {
                return leadItem;
            }

            return read_status_update({
                ...leadItem,
                ...lead
            }, prospectStatus);
        });
        const updatedActiveLead = read_status_update(lead, prospectStatus);
        const shouldOpenSlider = prospectStatus === prospectLeadInProgressStatus;

        if (!shouldOpenSlider) setLeads(updatedLeads);

        if (!shouldOpenSlider && read_sales_row_matches_key(lead, activeLeadId)) {
            setDraftLead(updatedActiveLead);
        }

        const updatedProspect = await update_brand_status(lead, prospectStatus).catch(error => {
            return { blocked: true, cancelled: Boolean(error.cancelled), message: error?.response?.data?.message || error.message };
        });

        if (!updatedProspect || updatedProspect?.blocked) {
            setLeads(store.getState()?.user?.user?.sub === currentUser.sub ? revoke_contact_display_payload(previousLeads) : []);
            if (shouldOpenSlider) {
                setActiveLeadId(previousActiveLeadId);
                setDraftLead(store.getState()?.user?.user?.sub === currentUser.sub ? revoke_contact_display_payload(previousDraftLead) : null);
            }

            if (!shouldOpenSlider && read_sales_row_matches_key(lead, activeLeadId)) {
                setDraftLead(lead);
            }

            if (!updatedProspect?.cancelled) window.alert(read_move_failure_message(updatedProspect));

            return false;
        }

        if (shouldOpenSlider) {
            setActiveLeadId(targetLeadKey);
            reset_progress_action();
        }
        const updatedActiveRow = read_updated_active_row(updatedProspect, lead);

        setLeads((currentLeads) => read_rows_with_updated_prospect(currentLeads, updatedProspect));
        if (updatedActiveRow && (shouldOpenSlider || read_sales_row_matches_key(updatedActiveRow, activeLeadId))) {
            setDraftLead(updatedActiveRow);
        }

        show_sync_warning(updatedProspect?.sync);

        if ([prospectLeadBacklogStatus, prospectLeadLostStatus].includes(prospectStatus)) {
            refresh_board_members();
        }

        return true;
    };

    const claim_unassigned_lead = (lead) => {
        if (!currentUserIsBoardTeamMember || read_member_keys(read_lead_assignee(lead)).length || claimingLeadId) {
            return false;
        }

        return run_sales_submission(`claim:${read_sales_row_key(lead)}`, async () => {
            setClaimingLeadId(read_sales_row_key(lead));

            try {
                const sellerPayload = read_member_assignment_payload(activeClockMember || currentUser);
                const updatedProspect = await assign_brand_seller(lead, sellerPayload, read_prospect_status(lead));

                if (!updatedProspect || updatedProspect?.blocked) {
                    window.alert(updatedProspect?.message || updatedProspect?.msg || t("This Sales card was already claimed."));
                    return false;
                }

                setLeads((currentLeads) => read_rows_with_updated_prospect(currentLeads, updatedProspect));
                refresh_board_members();
                return true;
            } catch (error) {
                if (!error.cancelled) window.alert(error?.response?.data?.message || error?.response?.data?.msg || t("This Sales card was already claimed."));
                return false;
            } finally {
                setClaimingLeadId("");
            }
        });
    };

    /**
     * Changes the active slider lead status unless the lead is phone-only.
     */
    const change_active_lead_status = (prospectStatus) => {
        if (activeLeadIsPhoneOnly) {
            return;
        }

        run_sales_submission("change_status", () => move_lead_to_status(activeSliderLead, prospectStatus));
    };

    /**
     * Saves edits from the slider and keeps the board state in sync.
     */
    const edit_active_lead = async (updatedLead) => {
        log_sales_ui_debug("edit_active_lead:start", {
            activeLeadIsInProgress,
            updated: read_sales_debug_row(updatedLead),
            activeKey: activeLeadId
        });

        if (!activeLeadIsInProgress) {
            log_sales_ui_debug("edit_active_lead:blocked_not_in_progress", {
                updated: read_sales_debug_row(updatedLead)
            });
            return false;
        }

        const previousLeads = leads;
        const updatedLeads = leads.map((leadItem) => {
            if (!read_sales_rows_match(leadItem, updatedLead)) {
                return leadItem;
            }

            return updatedLead;
        });

        setLeads(updatedLeads);
        if (read_sales_row_matches_key(updatedLead, activeLeadId)) {
            setDraftLead(updatedLead);
        }

        log_sales_ui_debug("edit_active_lead:optimistic_state_set", {
            updated: read_sales_debug_row(updatedLead),
            previousCount: previousLeads.length,
            nextCount: updatedLeads.length
        });

        let saveError = null;
        const updatedProspect = await edit_prospect_brand(updatedLead).catch((error) => {
            saveError = error;
            return false;
        });

        if (!updatedProspect) {
            console.error("[Niffler Sales UI] edit_active_lead:api_failed", {
                updated: read_sales_debug_row(updatedLead),
                status: saveError?.response?.status || 0,
                backendMessage: saveError?.response?.data?.message || saveError?.response?.data?.msg || "",
                message: saveError?.message || ""
            });
            setLeads(store.getState()?.user?.user?.sub === currentUser.sub ? revoke_contact_display_payload(previousLeads) : []);
            if (read_sales_row_matches_key(updatedLead, activeLeadId)) {
                setDraftLead(store.getState()?.user?.user?.sub === currentUser.sub ? revoke_contact_display_payload(updatedLead) : null);
            }
            setProgressActionError(read_request_error_message(saveError, t("Could not save changes")));
            return false;
        }

        const updatedActiveRow = read_updated_active_row(updatedProspect, updatedLead);

        log_sales_ui_debug("edit_active_lead:api_success", {
            updated: read_sales_debug_row(updatedLead),
            returned: read_sales_debug_row(updatedActiveRow),
            prospectId: updatedProspect?._id || "",
            syncWarning: updatedProspect?.sync?.warning || ""
        });

        setLeads((currentLeads) => read_rows_with_updated_prospect(currentLeads, updatedProspect));
        if (updatedActiveRow) {
            setDraftLead(updatedActiveRow);
        }

        show_sync_warning(updatedProspect?.sync);

        return updatedActiveRow || updatedLead;
    };

    /**
     * Persists all draft edits currently visible in the slider.
     */
    const save_active_lead = (updatedLead = activeSliderLead) => {
        log_sales_ui_debug("save_active_lead:click", {
            active: read_sales_debug_row(updatedLead)
        });

        return run_sales_submission("save_lead", () => edit_active_lead(updatedLead));
    };

    /**
     * Creates an action entry attached to the current lead outcome.
     */
    const create_progress_lead_action = async (action, prospectStatus, schedule, comment, offer, outcome = progressAction) => {
        const response = await create_brand_comment(activeSliderLead, comment, prospectStatus).catch(() => false);
        const updatedProspect = response?.prospect;
        const updatedActiveRow = updatedProspect ? read_updated_active_row(updatedProspect, activeSliderLead) : null;

        if (!updatedProspect) {
            return false;
        }

        setLeads((currentLeads) => read_rows_with_updated_prospect(currentLeads, updatedProspect));
        if (updatedActiveRow) {
            setDraftLead(updatedActiveRow);
        }

        return response?.comment || true;
    };

    /**
     * Saves the default Not Answered outcome and returns the lead to To Call.
     */
    const save_not_answered_progress_action = async () => {
        const moved = await move_lead_to_status(activeSliderLead, prospectLeadAssignedStatus);

        if (moved) {
            close_lead_slider();
        }
    };

    /**
     * Saves the selected callback date and moves the lead into Stalling.
     */
    const save_reschedule_progress_action = async () => {
        const author = read_comment_author();
        const response = await create_brand_calendar({
            prospectId: activeSliderLead?.prospectId,
            brandId: activeSliderLead?.brandId || activeSliderLead?._id,
            schedule: progressActionDate,
            seller: {
                userId: author.userId,
                name: author.name
            },
            comment: read_reschedule_comment()
        }).catch(() => false);
        const action = response?.action;
        const prospect = response?.prospect;
        const lead = prospect ? read_updated_active_row(prospect, activeSliderLead) : null;

        if (!action || !lead) {
            return;
        }

        setLeads((currentLeads) => read_rows_with_updated_prospect(currentLeads, prospect));
        setDraftLead(lead);

        if (response?.godfatherSync?.synced === false) {
            setProgressActionError(t("SASS saved but Offers card was not created"));
            return;
        }

        close_lead_slider();
    };

    /**
     * Applies a final Sales outcome and clears the stale active slider row.
     */
    const apply_terminal_sales_outcome = (prospect) => {
        if (!prospect) {
            return false;
        }

        setLeads((currentLeads) => read_rows_with_updated_prospect(currentLeads, prospect));
        close_lead_slider();
        refresh_board_members();

        return true;
    };

    /**
     * Sends a Godfather draft prefill after closing Sales as Agency.
     */
    const save_create_offer_progress_action = async (savedLead, pendingOfferWindow = null, handoffId = "") => {
        log_sales_ui_debug("create_offer_progress_action:start", {
            handoffId,
            saved: read_sales_debug_row(savedLead)
        });

        let createOfferError = null;
        const response = await create_offer_from_brand(savedLead).catch((error) => {
            createOfferError = error;
            return false;
        });
        const offer = response?.offer;

        if (!offer) {
            console.error("[Niffler Sales UI] create_offer_progress_action:api_failed", {
                handoffId,
                saved: read_sales_debug_row(savedLead),
                status: createOfferError?.response?.status || 0,
                backendMessage: createOfferError?.response?.data?.message || createOfferError?.response?.data?.msg || "",
                message: createOfferError?.message || ""
            });
            close_pending_offer_window(pendingOfferWindow);
            setProgressActionError(read_request_error_message(createOfferError, t("Could not create offer")));
            return false;
        }

        log_sales_ui_debug("create_offer_progress_action:api_success", {
            handoffId,
            saved: read_sales_debug_row(savedLead),
            hasOffer: Boolean(offer),
            prospectId: response?.prospect?._id || ""
        });

        redirect_godfather_offer_prefill(response, pendingOfferWindow);
        post_godfather_offer_prefill(response, pendingOfferWindow, handoffId);
        const applied = apply_terminal_sales_outcome(response?.prospect);

        if (!applied) {
            setProgressActionError(t("Could not create offer"));
            return false;
        }

        setProgressActionError("");

        return true;
    };

    /**
     * Saves the required Lost comment and moves the lead into Lost.
     */
    const save_not_interested_progress_action = async () => {
        const action = await create_progress_lead_action("Note", prospectLeadLostStatus, false, progressActionComment.trim());

        if (!action) {
            return;
        }

        const moved = await move_lead_to_status(activeSliderLead, prospectLeadLostStatus);

        if (moved) {
            close_lead_slider();
        }
    };

    /**
     * Saves the temporary SASS outcome with a meeting date and closes Sales as Sass.
     */
    const save_sass_progress_action = async () => {
        const author = read_comment_author();
        const caller = read_team_member_from_seller_value(teamMembers, sassCallerValue);
        const callerPayload = read_member_assignment_payload(caller || {});
        const response = await create_sass_from_brand(activeSliderLead, progressActionDate, {
            userId: author.userId,
            name: author.name
        }, callerPayload).catch((error) => ({ error }));
        const prospect = response?.prospect;
        const lead = prospect ? read_updated_active_row(prospect, activeSliderLead) : null;

        if (response?.error) {
            const requestErrorMessage = read_sass_request_error_message(response.error, t("Could not save SASS meeting"));
            const requestErrorCode = response.error?.response?.data?.errorCode;

            if (requestErrorCode === "SASS_ACTIVE_SUBSCRIPTION") {
                show_sass_active_customer_warning(requestErrorMessage);
                return;
            }

            setProgressActionError(requestErrorMessage);
            return;
        }

        const currentActiveCustomerState = read_sass_active_customer_state(activeSliderLead);
        const savedActiveCustomerState = response?.sassActiveCustomerState || {};

        if (savedActiveCustomerState.message
            && savedActiveCustomerState.message !== currentActiveCustomerState.message) {
            if (savedActiveCustomerState.blocked) {
                show_sass_active_customer_warning(savedActiveCustomerState.message);
            } else {
                show_sass_active_customer_notice(savedActiveCustomerState.message);
            }
        }

        if (response?.godfatherSync && response.godfatherSync?.synced !== true && response.godfatherSync?.queued !== true) {
            setProgressActionError(`${t("Offers SASS card was not created")}: ${response.godfatherSync?.error || t("Unknown error")}`);
            return;
        }

        if (response?.godfatherSync?.queued === true) {
            alertMessage(alerts, setAlerts, t("Saved, sync pending"), "Warning");
        }

        if (!lead) {
            setProgressActionError(t("Could not save SASS meeting"));
            return;
        }

        apply_terminal_sales_outcome(prospect);
    };

    /**
     * Saves visible brand edits as a timeline update and removes it from Sales.
     */
    const save_update_progress_action = async () => {
        log_sales_ui_debug("save_update_progress_action:start", {
            active: read_sales_debug_row(activeSliderLead)
        });

        const savedLead = await edit_active_lead(activeSliderLead);

        if (!savedLead) {
            log_sales_ui_debug("save_update_progress_action:edit_failed", {
                active: read_sales_debug_row(activeSliderLead)
            });
            setProgressActionError(t("Could not save update"));
            return;
        }

        const updatedProspect = await update_brand_status(savedLead, prospectLeadBacklogStatus, savedLead, prospectLeadProgressActionUpdate).catch(() => false);

        if (!updatedProspect) {
            log_sales_ui_debug("save_update_progress_action:status_failed", {
                saved: read_sales_debug_row(savedLead)
            });
            setProgressActionError(t("Could not save update"));
            return;
        }

        log_sales_ui_debug("save_update_progress_action:success", {
            saved: read_sales_debug_row(savedLead),
            prospectId: updatedProspect?._id || ""
        });

        setLeads((currentLeads) => read_rows_with_updated_prospect(currentLeads, updatedProspect));
        show_sync_warning(updatedProspect?.sync);
        close_lead_slider();
        refresh_board_members();
    };

    /**
     * Prevents shared action wrappers from submitting any parent form element.
     */
    const prevent_sales_action_default = (event) => {
        event?.preventDefault?.();
    };

    /**
     * Validates and executes the selected In Progress save outcome.
     */
    const save_in_progress_outcome = (event) => {
        prevent_sales_action_default(event);

        return run_sales_submission(`save_outcome:${progressAction}`, async () => {
            log_sales_ui_debug("save_in_progress_outcome:start", {
                action: progressAction,
                active: read_sales_debug_row(activeSliderLead)
            });

            const error = read_progress_action_error();

            if (error) {
                log_sales_ui_debug("save_in_progress_outcome:validation_error", {
                    action: progressAction,
                    error,
                    active: read_sales_debug_row(activeSliderLead)
                });
                setProgressActionError(error);
                return false;
            }

            if (progressAction === prospectLeadProgressActionCreateOffer) {
                return start_create_offer_handoff();
            }

            if (progressAction === prospectLeadProgressActionUpdate) {
                return save_update_progress_action();
            }

            if (progressAction === prospectLeadProgressActionSass) {
                const activeCustomerState = read_sass_active_customer_state(activeSliderLead);

                if (activeCustomerState.message) {
                    if (!activeCustomerState.blocked) {
                        show_sass_active_customer_notice(activeCustomerState.message);
                    } else {
                        show_sass_active_customer_warning(activeCustomerState.message);
                    }
                }

                if (activeCustomerState.blocked) {
                    return false;
                }
            }

            const saved = await edit_active_lead(activeSliderLead);

            if (!saved) {
                log_sales_ui_debug("save_in_progress_outcome:edit_failed", {
                    action: progressAction,
                    active: read_sales_debug_row(activeSliderLead)
                });
                return false;
            }

            log_sales_ui_debug("save_in_progress_outcome:edit_saved", {
                action: progressAction,
                saved: read_sales_debug_row(saved)
            });

            const saveProgressAction = {
                [prospectLeadProgressActionNotAnswered]: save_not_answered_progress_action,
                [prospectLeadProgressActionReschedule]: save_reschedule_progress_action,
                [prospectLeadProgressActionSass]: save_sass_progress_action,
                [prospectLeadProgressActionNotInterested]: save_not_interested_progress_action,
                [prospectLeadProgressActionUpdate]: save_update_progress_action
            }[progressAction] || save_not_answered_progress_action;

            return saveProgressAction();
        });
    };

    /**
     * Saves Backlog/To Call phone-only edits, then closes the slider.
     */
    const finish_phone_only_lead = async () => {
        const updatedLead = read_done_lead(activeSliderLead);
        const saved = await edit_active_lead(updatedLead);

        if (!saved) {
            return;
        }

        setPendingPhoneValue("");
        close_lead_slider();
    };

    /**
     * Adds one rich-text comment as a saved lead action.
     */
    const add_active_lead_comment = (commentHtml) => {
        if (!activeLeadIsInProgress) {
            return false;
        }

        return run_sales_submission("add_comment", async () => {
            const savedLead = await edit_active_lead(activeSliderLead);

            if (!savedLead) {
                return false;
            }

            const prospectStatus = read_prospect_status(savedLead);
            const response = await create_brand_comment(savedLead, commentHtml, prospectStatus).catch(() => false);
            const updatedProspect = response?.prospect;
            const updatedActiveRow = updatedProspect ? read_updated_active_row(updatedProspect, savedLead) : null;

            if (!updatedActiveRow) {
                return false;
            }

            setDraftLead(updatedActiveRow);
            setLeads((currentLeads) => read_rows_with_updated_prospect(currentLeads, updatedProspect));

            return true;
        });
    };

    /**
     * Opens the detail slider unless the click came from a drag release.
     */
    const open_lead_from_card = (lead) => {
        if (dragClickLockRef.current) {
            return;
        }

        setActiveLeadId(read_sales_row_key(lead));
        setDraftLead(lead);
        setPendingPhoneValue("");
        reset_progress_action();
    };

    /**
     * Closes the lead detail slider.
     */
    const close_lead_slider = () => {
        setActiveLeadId("");
        setDraftLead(null);
        setPendingPhoneValue("");
        reset_progress_action();
    };

    /**
     * Accepts Godfather completion messages after its backend finalizes Niffler.
     */
    const receive_godfather_offer_completed = (event) => {
        const godfatherOrigin = read_godfather_origin();
        const message = event?.data || {};
        const completedProspectRows = message?.prospect ? read_prospect_brand_rows([message.prospect]) : [];
        const completedLeadFromProspect = completedProspectRows.find((row) => {
            return read_sales_row_matches_key(row, activeLeadId) || read_sales_rows_match(row, activeSliderLead);
        }) || completedProspectRows[0] || null;
        const completedLead = completedLeadFromProspect || message?.lead;

        if (godfatherOrigin !== "*" && event?.origin !== godfatherOrigin) {
            return;
        }

        if (message?.type !== "niffler_offer_completed" || !completedLead?._id) {
            return;
        }

        setLeads((currentLeads) => {
            if (message?.prospect) {
                return read_rows_with_updated_prospect(currentLeads, message.prospect);
            }

            return currentLeads.map((leadItem) => {
                if (!read_sales_rows_match(leadItem, completedLead)) {
                    return leadItem;
                }

                return completedLead;
            });
        });

        if (read_sales_row_matches_key(completedLead, activeLeadId)) {
            setDraftLead(completedLead);
            close_lead_slider();
        }

        refresh_board_members();
    };

    /**
     * Blocks manual close while In Progress requires a saved outcome.
     */
    const prevent_in_progress_slider_close = () => {
        setProgressActionError(t("Choose an outcome and save before closing"));
    };

    /**
     * Starts dragging one lead using the same dataTransfer pattern as Cerebro.
     */
    const start_drag_lead = (event, leadId) => {
        const lead = leads.find((leadItem) => read_sales_row_matches_key(leadItem, leadId));

        if (read_is_backlog_lead(lead)) {
            event.preventDefault();
            return;
        }

        dragLeadIdRef.current = leadId;
        dragClickLockRef.current = true;
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", leadId);
        setDragLeadId(leadId);
    };

    /**
     * Clears dragging state and unlocks card clicks shortly after drop.
     */
    const release_drag_lead = (lockDelay = 150) => {
        setDragLeadId("");
        dragLeadIdRef.current = "";

        window.setTimeout(() => {
            dragClickLockRef.current = false;
        }, lockDelay);
    };

    /**
     * Clears the dragging marker after a drag operation ends.
     */
    const finish_drag_lead = () => {
        release_drag_lead();
    };

    /**
     * Enables browser drop behavior for the board columns.
     */
    const allow_lead_drop = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    /**
     * Reads the dropped lead id from dataTransfer, then ref/state fallbacks.
     */
    const read_drag_lead_id = (event) => {
        const transferredLeadId = `${event?.dataTransfer?.getData("text/plain") || ""}`.trim();

        return transferredLeadId || dragLeadIdRef.current || dragLeadId;
    };

    /**
     * Accepts one dropped lead into the target prospect column.
     */
    const drop_lead_on_column = (event, prospectStatus) => {
        event.preventDefault();
        event.stopPropagation();

        const droppedLeadId = read_drag_lead_id(event);

        if (!droppedLeadId) {
            release_drag_lead();
            return;
        }

        const droppedLead = leads.find((lead) => read_sales_row_matches_key(lead, droppedLeadId));

        if (!droppedLead) {
            release_drag_lead();
            return;
        }

        if (read_is_backlog_lead(droppedLead) && read_prospect_status(droppedLead) !== prospectStatus) {
            release_drag_lead();
            return;
        }

        if (`${read_prospect_status(droppedLead) || ""}` === `${prospectStatus || ""}`) {
            release_drag_lead();
            return;
        }

        release_drag_lead();
        run_sales_submission("move_lead", () => move_lead_to_status(droppedLead, prospectStatus));
    };

    /**
     * Toggles one team-member avatar filter.
     */
    const toggle_team_member = (member) => {
        const memberKeys = read_member_keys(member);
        const selected = memberKeys.some((memberKey) => selectedTeamMemberKeys.includes(memberKey));
        const nextSelectedTeamMemberKeys = selected
            ? selectedTeamMemberKeys.filter((memberKey) => !memberKeys.includes(memberKey))
            : [...selectedTeamMemberKeys, ...memberKeys];

        salesSellerFilterKeyRef.current = read_sales_seller_filter_key(nextSelectedTeamMemberKeys);
        setSelectedTeamMemberKeys(nextSelectedTeamMemberKeys);
        load_new_leads(1, "replace", nextSelectedTeamMemberKeys);
    };

    useEffect(() => {
        const clear_contacts = () => {
            salesRequestSequenceRef.current += 1;
            setLeads([]);
            setDraftLead(null);
            setActiveLeadId('');
        };
        const unsubscribe = store.subscribe(() => {
            if (store.getState()?.user?.user?.sub !== currentUser.sub) clear_contacts();
        });
        return () => { unsubscribe(); salesRequestSequenceRef.current += 1; };
    }, [currentUser.sub]);

    useEffect(() => {
        register_sales_push_notifications();
        setLoading(true);

        Promise.all([
            get_sales_prospects(1, pageSize),
            read_dolores_team_users().catch(() => [])
        ])
            .then(([response, loadedTeamMembers]) => {
                if (store.getState()?.user?.user?.sub !== currentUser.sub) return;
                const fetchedLeads = read_unique_sales_rows(read_prospect_brand_rows(response?.prospects || [], true));
                const fetchedTotal = response?.count || 0;

                setLeads(fetchedLeads);
                setTotalLeads(fetchedTotal);
                setHasMoreSalesPages(pageSize < fetchedTotal);
                setTeamMembers(loadedTeamMembers || []);
                setBoardMembers(read_sales_board_members(response?.assignedSellers, fetchedLeads));
                setPageNo(1);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!activeClockMemberKey) {
            return;
        }

        load_member_clock(activeClockMember || {});
        const closeClockEvents = open_hr_member_clock_events(currentUser, activeClockMember || {}, (clockState) => {
            update_member_clock_state(clockState || { loaded: false });
        });
        const silentClockSyncInterval = window.setInterval(() => {
            load_member_clock(activeClockMember || {});
        }, silentSalesClockSyncIntervalMs);

        return () => {
            closeClockEvents();
            window.clearInterval(silentClockSyncInterval);
        };
    }, [activeClockMemberKey]);

    useEffect(() => {
        const availableMemberKeys = boardMembers.flatMap((member) => read_member_keys(member));

        setSelectedTeamMemberKeys((currentKeys) => currentKeys.filter((memberKey) => availableMemberKeys.includes(memberKey)));
    }, [boardMembers]);

    useEffect(() => {
        const loadMoreTrigger = loadMoreTriggerRef.current;

        if (!loadMoreTrigger || !showLoadMore || loading) {
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                load_more_new_leads();
            }
        }, {
            root: null,
            rootMargin: "20% 0%",
            threshold: 0
        });

        observer.observe(loadMoreTrigger);

        return () => {
            observer.disconnect();
        };
    }, [showLoadMore, loading, pageNo, leads.length, load_more_new_leads]);

    useEffect(() => {
        window.addEventListener("message", receive_godfather_offer_completed);

        return () => {
            window.removeEventListener("message", receive_godfather_offer_completed);
        };
    }, [activeLeadId, activeSliderLead?.brandId]);

    const activeSliderActions = {
        backlog: {
            text: t("Done"),
            icon: "Check",
            onClick: close_lead_slider
        },
        assigned: {
            text: t("Done"),
            icon: "Check",
            onClick: close_lead_slider
        },
        inProgress: {
            text: t("Save"),
            icon: "Check",
            onClick: save_in_progress_outcome
        },
        editable: {
            text: t("Done"),
            icon: "Check",
            onClick: close_lead_slider
        }
    }[activeLeadIsBacklog ? "backlog" : activeLeadIsAssigned ? "assigned" : activeLeadIsInProgress ? "inProgress" : "editable"];
    const salesClockBarNode = (
        <Sales_Clock_Bar
            clock={memberClock}
            attention={Boolean(salesClockLockEnabled && read_clock_loaded(memberClock || {}) && !read_clock_ticking(memberClock || {}))}
            loading={clockActionLoading}
            onToggleClock={toggle_member_clock}
        />
    );
    const salesLockedOverlayNode = (
        <ProspectLeadsClockOverlay>
            <ProspectLeadsClockCard>
                <h2>{t("Keep your HR clock running")}</h2>
                <p>{t("The Sales board stays locked while your HR clock is paused or not started.")}</p>
            </ProspectLeadsClockCard>
        </ProspectLeadsClockOverlay>
    );

    return (
        <ProspectLeads>
            <Alert_ duration={6000} />
            <Toolbar_
                sidebar={sidebar}
                activeOption="sales"
                onSidebar={() => setSidebar(!sidebar)}
                routeLanguage={routeLanguage}
                onInternalNavigate={navigate}
            />
            <PageSidebar>
                <PageAlwaysVisible>
                    <ProspectLeadsContent>
                        <PageTitle>
                            <Grey_Link iconLeft="Back" onClick={() => navigate(`/${routeLanguage}/`)} />
                            <h1>{t("Sales")}</h1>
                        </PageTitle>

                        <Sales_Header
                            searchTerm={searchTerm}
                            onSearchTerm={setSearchTerm}
                            teamMembers={boardMembers}
                            selectedTeamMemberKeys={selectedTeamMemberKeys}
                            onTeamMember={toggle_team_member}
                        />

                        <ProspectLeadsBoardStage>
                            {salesClockBarNode}
                            <ProspectLeadsBoardStageContent locked={clockBlocked || salesSubmissionLocked}>
                                {loading ? <Partial_Loader text={t("Loading leads")} /> : null}

                                {!loading && visibleLeads.length === 0 ? (
                                    <ProspectLeadsEmpty>
                                        <h5>{t("No sales leads yet")}</h5>
                                    </ProspectLeadsEmpty>
                                ) : null}

                                {visibleLeads.length > 0 ? (
                                    <Sales_Board
                                        boardColumns={boardColumns}
                                        teamMembers={teamMembers}
                                        dragLeadId={dragLeadId}
                            onDrop={drop_lead_on_column}
                            onDragOver={allow_lead_drop}
                            onDragStart={start_drag_lead}
                            onDragEnd={finish_drag_lead}
                                        onOpenLead={open_lead_from_card}
                                        canClaim={currentUserIsBoardTeamMember}
                                        claimingLeadId={claimingLeadId}
                                        onClaim={claim_unassigned_lead}
                                        attentionColumnStatus={clockNeedsInProgressAttention ? prospectLeadInProgressStatus : ""}
                                    />
                                ) : null}
                            </ProspectLeadsBoardStageContent>
                            {clockBlocked ? salesLockedOverlayNode : null}
                        </ProspectLeadsBoardStage>

                        {showLoadMore ? (
                            <ProspectLeadsLoadMoreTrigger ref={loadMoreTriggerRef} />
                        ) : null}
                    </ProspectLeadsContent>
                </PageAlwaysVisible>
            </PageSidebar>
            {activeSliderLead ? (
                <Slider_
                    title={activeSliderLead?.brand || read_lead_name(activeSliderLead) || t("Unnamed lead")}
                    onClose={salesSubmissionLocked ? prevent_sales_action_default : activeLeadIsInProgress ? prevent_in_progress_slider_close : close_lead_slider}
                    {...activeSliderActions}
                    text={salesSubmissionLocked ? t("Saving...") : activeSliderActions.text}
                    disabled={salesSubmissionLocked}
                >
                    <ProspectLeadSubmissionContent locked={salesSubmissionLocked} aria-busy={salesSubmissionLocked}>
                        <Sales_Lead_Form_Edit
                            lead={activeSliderLead}
                            teamMembers={teamMembers}
                            backlog={activeLeadIsBacklog}
                            inProgress={activeLeadIsInProgress}
                            phoneOnly={activeLeadIsPhoneOnly}
                            progressAction={progressAction}
                            progressActionDate={progressActionDate}
                            progressActionComment={progressActionComment}
                            progressActionError={progressActionError}
                            sassCaller={sassCallerValue}
                            onStatusChange={change_active_lead_status}
                            onPendingPhoneValue={setPendingPhoneValue}
                            onProgressAction={change_progress_action}
                            onProgressActionDate={change_progress_action_date}
                            onProgressActionComment={change_progress_action_comment}
                            onSassCaller={change_sass_caller}
                            onDraftLead={setDraftLead}
                            onSaveLead={save_active_lead}
                            onAddComment={add_active_lead_comment}
                        />
                    </ProspectLeadSubmissionContent>
                </Slider_>
            ) : null}
        </ProspectLeads>
    );
}

export { SALES };
